import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  FiberNew,
  History,
  FavoriteBorder,
  Star,
  Casino,
  Kitchen,
  Timer,
  Cake,
  Public,
  Restaurant,
} from '@mui/icons-material';
import ChoiceCard from '../components/ChoiceCard/ChoiceCard';
import MealsContext from '../context/meals-context';
import Meal from '../models/meal';
import StarRating from '../components/Meal/StarRating';
import HealthRating from '../components/Meal/HealthRating';

type FilterCard = {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  route?: string;
  absoluteRoute?: string;
  action?: string;
  disabled?: boolean;
  badge?: string;
};

const filters: FilterCard[] = [
  { icon: <FiberNew />, title: 'Něco nového', route: 'new' },
  { icon: <History />, title: 'Něco, co jsem dlouho nevařil/a', route: 'last-cooked' },
  { icon: <FavoriteBorder />, title: 'Něco zdravého', route: 'healthy' },
  { icon: <Star />, title: 'Jeden z oblíbených', route: 'favorites' },
  { icon: <Casino />, title: 'Překvap mě!', action: 'surprise' },
  { icon: <Kitchen />, title: 'Podle toho, co mám doma', absoluteRoute: '/cook-with-what-i-have' },
  { icon: <Public />, title: 'Podle kuchyně', absoluteRoute: '/cooking-guidance/by-cuisine' },
  {
    icon: <Restaurant />,
    title: 'Ostatní',
    absoluteRoute: '/cooking-guidance/by-type',
    subtitle: 'polévka, dezert, nápoj...',
  },
  { icon: <Timer />, title: 'Něco rychlého', disabled: true, badge: 'už brzy' },
  { icon: <Cake />, title: 'Něco sladkého / slaného', disabled: true, badge: 'už brzy' },
];

const pickRandomMeal = (meals: Meal[]): Meal | null => {
  if (meals.length === 0) return null;
  const index = Math.floor(Math.random() * meals.length);
  return meals[index];
};

const CookingGuidance = () => {
  const navigate = useNavigate();
  const { meals } = useContext(MealsContext);
  const [surpriseOpen, setSurpriseOpen] = useState(false);
  const [surpriseMeal, setSurpriseMeal] = useState<Meal | null>(null);

  const handleCardClick = (filter: FilterCard) => {
    if (filter.action === 'surprise') {
      const randomMeal = pickRandomMeal(meals);
      setSurpriseMeal(randomMeal);
      setSurpriseOpen(true);
    } else if (filter.absoluteRoute) {
      navigate(filter.absoluteRoute);
    } else if (filter.route) {
      navigate(`/cooking-guidance/${filter.route}`);
    }
  };

  const handleTryAgain = () => {
    const randomMeal = pickRandomMeal(meals);
    setSurpriseMeal(randomMeal);
  };

  const handleCloseSurprise = () => {
    setSurpriseOpen(false);
    setSurpriseMeal(null);
  };

  const mealImage = surpriseMeal
    ? surpriseMeal.imageThumbnail || surpriseMeal.image || '/images/blank-meal.jpg'
    : '/images/blank-meal.jpg';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 2 }}>
      <Typography variant="h5" textAlign="center" fontWeight={600} sx={{ mb: 3 }}>
        Co bys chtěl/a dnes vařit?
      </Typography>

      <Grid container spacing={2}>
        {filters.map((filter) => (
          <Grid item xs={6} md={4} key={filter.title}>
            <ChoiceCard
              icon={filter.icon}
              title={filter.title}
              subtitle={filter.subtitle}
              variant="medium"
              disabled={filter.disabled}
              badge={filter.badge}
              onClick={() => handleCardClick(filter)}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={surpriseOpen} onClose={handleCloseSurprise} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600 }}>Překvap mě!</DialogTitle>
        <DialogContent>
          {surpriseMeal ? (
            <Stack alignItems="center" spacing={1.5}>
              <CardMedia
                component="img"
                image={mealImage}
                alt={surpriseMeal.name}
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
              <Typography variant="h5" textAlign="center" fontWeight={600}>
                {surpriseMeal.name}
              </Typography>
              {surpriseMeal.tastiness !== null && <StarRating value={surpriseMeal.tastiness} size="small" />}
              {surpriseMeal.healthiness !== null && <HealthRating value={surpriseMeal.healthiness} size="small" />}
            </Stack>
          ) : (
            <Typography textAlign="center" color="text.secondary" sx={{ py: 2 }}>
              Nemáš zatím žádné recepty.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
          {surpriseMeal && (
            <>
              <Button variant="contained" onClick={() => navigate(`/meal-detail/${surpriseMeal.id}`)}>
                Zobrazit recept
              </Button>
              <Button variant="outlined" onClick={handleTryAgain}>
                Zkusit znovu
              </Button>
            </>
          )}
          {!surpriseMeal && (
            <Button variant="outlined" onClick={handleCloseSurprise}>
              Zavřít
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CookingGuidance;
